import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { AnimatePresence } from 'framer-motion';
import { SlideNavigation } from './components/SlideNavigation';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { CoverSlide } from './components/slides/CoverSlide';
import { ExecutiveSummary } from './components/slides/ExecutiveSummary';
import { AuditScorecard } from './components/slides/AuditScorecard';
import { RevenueBlockers } from './components/slides/RevenueBlockers';
import { LocalSEO } from './components/slides/LocalSEO';
import { GrowthStrategy } from './components/slides/GrowthStrategy';
import { TechnicalFixes } from './components/slides/TechnicalFixes';
import { ContentStrategy } from './components/slides/ContentStrategy';
import { CompetitorBenchmark } from './components/slides/CompetitorBenchmark';
import { MonthlySEOPlan } from './components/slides/MonthlySEOPlan';
import { WhyDigilite } from './components/slides/WhyDigilite';
import { FinalCTA } from './components/slides/FinalCTA';
const SLIDES = [CoverSlide, ExecutiveSummary, AuditScorecard, RevenueBlockers, LocalSEO, GrowthStrategy, TechnicalFixes, ContentStrategy, CompetitorBenchmark, MonthlySEOPlan, WhyDigilite, FinalCTA];
export function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfSlideIndex, setPdfSlideIndex] = useState(-1);
  const [pdfRenderIndex, setPdfRenderIndex] = useState(-1);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  // Touch swipe state
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const isSwiping = useRef(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isGeneratingPDF) return;
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        setCurrentSlide(prev => Math.min(prev + 1, SLIDES.length - 1));
      } else if (e.key === 'ArrowLeft') {
        setCurrentSlide(prev => Math.max(prev - 1, 0));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isGeneratingPDF]);
  // Touch swipe handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    touchEndX.current = null;
    isSwiping.current = false;
  }, []);
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
    // Determine if this is a horizontal swipe (vs vertical scroll)
    if (touchStartX.current !== null && touchStartY.current !== null) {
      const deltaX = Math.abs(e.touches[0].clientX - touchStartX.current);
      const deltaY = Math.abs(e.touches[0].clientY - touchStartY.current);
      if (deltaX > deltaY && deltaX > 10) {
        isSwiping.current = true;
      }
    }
  }, []);
  const handleTouchEnd = useCallback(() => {
    if (isGeneratingPDF || touchStartX.current === null || touchEndX.current === null || !isSwiping.current) {
      touchStartX.current = null;
      touchStartY.current = null;
      touchEndX.current = null;
      isSwiping.current = false;
      return;
    }
    const swipeDistance = touchStartX.current - touchEndX.current;
    const SWIPE_THRESHOLD = 50;
    if (swipeDistance > SWIPE_THRESHOLD) {
      // Swiped left → next slide
      setCurrentSlide(prev => Math.min(prev + 1, SLIDES.length - 1));
    } else if (swipeDistance < -SWIPE_THRESHOLD) {
      // Swiped right → previous slide
      setCurrentSlide(prev => Math.max(prev - 1, 0));
    }
    touchStartX.current = null;
    touchStartY.current = null;
    touchEndX.current = null;
    isSwiping.current = false;
  }, [isGeneratingPDF]);
  const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));
  const waitFrames = (n = 2) => new Promise<void>(resolve => {
    let count = 0;
    const tick = () => {
      count++;
      if (count >= n) resolve();else requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  });
  const handleDownloadPDF = useCallback(async () => {
    if (isGeneratingPDF) return;
    setIsGeneratingPDF(true);
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'px',
      format: [1920, 1080]
    });
    try {
      for (let i = 0; i < SLIDES.length; i++) {
        setPdfSlideIndex(i);
        setPdfRenderIndex(i);
        // Wait for React to render the slide in the offscreen container
        await waitFrames(3);
        await sleep(500);
        const container = pdfContainerRef.current;
        if (!container) continue;
        const canvas = await html2canvas(container, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#FFFFFF',
          width: container.offsetWidth,
          height: container.offsetHeight,
          logging: false
        });
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      }
      pdf.save('Mortgage-Pro-Kingston-SEO-Proposal.pdf');
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setPdfRenderIndex(-1);
      setPdfSlideIndex(-1);
      setIsGeneratingPDF(false);
    }
  }, [isGeneratingPDF]);
  const CurrentSlideComponent = SLIDES[currentSlide];
  const PdfSlideComponent = pdfRenderIndex >= 0 ? SLIDES[pdfRenderIndex] : null;
  return <div className="relative w-full h-screen overflow-hidden bg-white text-slate-700 font-body" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} data-id="element-0">
      {/* Main visible slide with animations */}
      <div className="relative w-full h-full" data-id="element-1">
        <AnimatePresence mode="wait" data-id="element-2">
          <CurrentSlideComponent key={currentSlide} data-id="element-3" />
        </AnimatePresence>
      </div>

      {/* Offscreen PDF capture container — no AnimatePresence, no animations */}
      {PdfSlideComponent && <div ref={pdfContainerRef} className="pdf-capture" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      zIndex: -1,
      pointerEvents: 'none',
      overflow: 'hidden'
    }} data-id="element-4">
          <PdfSlideComponent data-id="element-5" />
        </div>}

      {isGeneratingPDF && <div className="fixed inset-0 bg-white/90 backdrop-blur-sm z-[60] flex flex-col items-center justify-center gap-4 pointer-events-none" data-id="element-6">
          <div className="w-12 h-12 border-4 border-gold-500/30 border-t-gold-500 rounded-full animate-spin" data-id="element-7" />
          <p className="text-lg font-heading font-semibold text-slate-900" data-id="element-8">
            Generating PDF…
          </p>
          <p className="text-sm text-slate-500" data-id="element-9">
            Capturing slide {pdfSlideIndex + 1} of {SLIDES.length}
          </p>
        </div>}

      <SlideNavigation current={currentSlide} total={SLIDES.length} onNext={() => setCurrentSlide(prev => Math.min(prev + 1, SLIDES.length - 1))} onPrev={() => setCurrentSlide(prev => Math.max(prev - 1, 0))} onGoTo={index => setCurrentSlide(index)} onDownloadPDF={handleDownloadPDF} isGeneratingPDF={isGeneratingPDF} data-id="element-10" />
    </div>;
}